import React, { useCallback, useEffect, useState } from 'react';
import { Input, Modal, Table, message } from 'antd';
import request from '../../api/request';
import { apiProductsByIdGETAPI } from '../../api/generated/产品管理';
import type { Product } from '../../types/product';
import ProductDetailDrawer from '../products/components/ProductDetailDrawer';
import OrderDetailDrawer from './OrderDetailDrawer';
import OrderFiltersBar from './OrderFiltersBar';
import { createOrderColumns } from './orderColumns';
import { defaultFilters, Order, OrderFilters } from './types';
import './index.css';

const OrdersPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<OrderFilters>(defaultFilters);
  const [detail, setDetail] = useState<Order | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [productDetail, setProductDetail] = useState<Product | null>(null);
  const [productDetailVisible, setProductDetailVisible] = useState(false);
  const [productDetailLoadingId, setProductDetailLoadingId] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize };
      if (filters.keyword.trim()) params.keyword = filters.keyword.trim();
      if (filters.status) params.status = filters.status;
      if (filters.sourcePlatform) params.sourcePlatform = filters.sourcePlatform;
      if (filters.clientStatus) params.clientStatus = filters.clientStatus;
      if (filters.platformStatus.trim()) params.platformStatus = filters.platformStatus.trim();
      if (filters.platformOrderNo.trim()) params.platformOrderNo = filters.platformOrderNo.trim();
      if (filters.name.trim()) params.name = filters.name.trim();
      if (filters.idCard.trim()) params.idCard = filters.idCard.trim();
      if (filters.productId.trim()) params.productId = Number(filters.productId.trim());
      if (filters.sourceProductId.trim()) params.sourceProductId = filters.sourceProductId.trim();
      if (filters.hasTrackingNo) params.hasTrackingNo = filters.hasTrackingNo;
      if (filters.hasCardNumber) params.hasCardNumber = filters.hasCardNumber;
      if (filters.exceptionStatus) params.exceptionStatus = filters.exceptionStatus;
      if (filters.exceptionOnly) params.exceptionOnly = filters.exceptionOnly;
      if (filters.dateRange) {
        params.createdStart = filters.dateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
        params.createdEnd = filters.dateRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
      }

      const data = await request({ url: '/api/sim-orders', method: 'GET', params }) as {
        items?: Order[];
        total?: number;
      };

      setOrders(data.items || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '获取订单列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateFilter = <K extends keyof OrderFilters>(key: K, value: OrderFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  const refreshDetail = (nextOrder: Order) => {
    setOrders((prev) => prev.map((item) => (item.id === nextOrder.id ? nextOrder : item)));
    setDetail(nextOrder);
  };

  const manualSyncOrder = async (order: Order) => {
    try {
      setActionLoadingId(order.id);
      const nextOrder = await request({ url: `/api/sim-orders/${order.id}/sync`, method: 'POST' }) as Order;
      refreshDetail(nextOrder);
      message.success('订单状态已同步');
    } catch (err: any) {
      message.error(err?.response?.data?.message || '同步失败');
    } finally {
      setActionLoadingId(null);
    }
  };

  const retryOrder = async (order: Order) => {
    Modal.confirm({
      title: '确认手动重提',
      content: `将沿用系统订单 ${order.orderNo} 重新提交到平台，不会生成新系统订单。`,
      okText: '确认重提',
      cancelText: '取消',
      onOk: async () => {
        try {
          setActionLoadingId(order.id);
          const nextOrder = await request({ url: `/api/sim-orders/${order.id}/retry`, method: 'POST' }) as Order;
          refreshDetail(nextOrder);
          message.success('手动重提已完成');
        } catch (err: any) {
          message.error(err?.response?.data?.message || '手动重提失败');
        } finally {
          setActionLoadingId(null);
        }
      },
    });
  };

  const updateAdminRemark = async (order: Order) => {
    let nextRemark = order.adminRemark || '';
    Modal.confirm({
      title: '运营备注',
      icon: null,
      content: (
        <Input.TextArea
          defaultValue={nextRemark}
          rows={5}
          maxLength={2000}
          showCount
          onChange={(event) => {
            nextRemark = event.target.value;
          }}
        />
      ),
      okText: '保存',
      cancelText: '取消',
      onOk: async () => {
        const nextOrder = await request({
          url: `/api/sim-orders/${order.id}/admin-remark`,
          method: 'PATCH',
          data: { adminRemark: nextRemark },
        }) as Order;
        refreshDetail(nextOrder);
        message.success('备注已保存');
      },
    });
  };

  const resolveException = async (order: Order) => {
    const nextOrder = await request({
      url: `/api/sim-orders/${order.id}/exception/resolve`,
      method: 'PATCH',
      data: { remark: '后台手动标记已处理' },
    }) as Order;
    refreshDetail(nextOrder);
    message.success('异常已标记处理');
  };

  const reopenException = async (order: Order) => {
    const nextOrder = await request({
      url: `/api/sim-orders/${order.id}/exception/reopen`,
      method: 'PATCH',
      data: { remark: '后台重新打开异常' },
    }) as Order;
    refreshDetail(nextOrder);
    message.success('异常已重新打开');
  };

  const batchManualSync = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择订单');
      return;
    }
    try {
      setLoading(true);
      const data = await request({
        url: '/api/sim-orders/batch-sync',
        method: 'POST',
        data: { ids: selectedRowKeys },
      }) as { message?: string };
      message.success(data.message || '批量同步完成');
      setSelectedRowKeys([]);
      fetchOrders();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '批量同步失败');
    } finally {
      setLoading(false);
    }
  };

  const showProductDetail = async (productId: number) => {
    if (!productId) {
      message.warning('该订单没有关联产品');
      return;
    }

    try {
      setProductDetailLoadingId(productId);
      const product = await apiProductsByIdGETAPI({ id: productId });
      setProductDetail(product as Product);
      setProductDetailVisible(true);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '获取产品详情失败');
    } finally {
      setProductDetailLoadingId(null);
    }
  };

  const columns = createOrderColumns({
    productDetailLoadingId,
    actionLoadingId,
    onShowDetail: (order) => {
      setDetail(order);
      setDetailVisible(true);
    },
    onShowProduct: showProductDetail,
    onManualSync: manualSyncOrder,
    onRetry: retryOrder,
  });

  return (
    <>
      <div className="orders-page">
        <OrderFiltersBar
          filters={filters}
          selectedCount={selectedRowKeys.length}
          onChange={updateFilter}
          onReset={resetFilters}
          onBatchSync={batchManualSync}
        />

        <div className="orders-table-wrap">
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ y: 'calc(100vh - 330px)' }}
            rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (count) => `共 ${count} 条`,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize);
              },
            }}
          />
        </div>
      </div>

      <OrderDetailDrawer
        detail={detail}
        open={detailVisible}
        productDetailLoadingId={productDetailLoadingId}
        actionLoadingId={actionLoadingId}
        onClose={() => setDetailVisible(false)}
        onShowProduct={showProductDetail}
        onManualSync={manualSyncOrder}
        onRetry={retryOrder}
        onUpdateRemark={updateAdminRemark}
        onResolveException={resolveException}
        onReopenException={reopenException}
      />

      <ProductDetailDrawer
        open={productDetailVisible}
        product={productDetail}
        platform172Detail={null}
        zIndex={1200}
        onClose={() => {
          setProductDetailVisible(false);
          setProductDetail(null);
        }}
      />
    </>
  );
};

export default OrdersPage;
